import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shell } from '../components/layout/Shell';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Check, ArrowRight } from '@phosphor-icons/react';

const Pricing = () => {
  const [plans, setPlans] = useState({});
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState({});
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get('/billing/plans').then((r) => setPlans(r.data));
    if (user?.role === 'employer') {
      api.get('/billing/me').then((r) => setMine(r.data)).catch(() => {});
    }
  }, [user]);

  const choose = async (packageId) => {
    if (!user) { nav('/register?role=employer'); return; }
    if (user.role !== 'employer') { toast.error('Only employers can subscribe'); return; }
    setLoading({ [packageId]: true });
    try {
      const { data } = await api.post('/billing/checkout', {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      if (data.free) {
        toast.success('Starter activated');
        nav('/employer/billing');
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
    } finally {
      setLoading({});
    }
  };

  const order = ['starter', 'team', 'scale'];
  const features = {
    starter: ['1 seat', '3 active jobs', 'Multi-source job feed', 'Core scoring engine', 'Community support'],
    team:    ['5 seats', '25 active jobs', 'AI score explanations', 'Work-style profiles', 'Applications inbox', 'Email support'],
    scale:   ['20 seats', '200 active jobs', 'Fairness audit dashboard', 'ML anti-gaming detector', 'Priority support', 'SSO (on request)'],
  };

  return (
    <Shell>
      <div className="max-w-[1300px] mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <div className="label-eyebrow text-primary mb-3">PRICING</div>
          <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter mb-4">Pay per team, not per hire.</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Start free. Upgrade when you're ready to see more candidates, more signal, more proof.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-0 grid-skeleton" data-testid="pricing-grid">
          {order.map((k) => {
            const p = plans[k];
            if (!p) return null;
            const isCurrent = mine?.plan === k;
            const isFeatured = k === 'team';
            return (
              <div
                key={k}
                className={`p-8 relative ${isFeatured ? 'bg-foreground text-background' : 'bg-white'}`}
                data-testid={`pricing-card-${k}`}
              >
                {isFeatured && (
                  <div className="absolute top-0 right-0 label-eyebrow bg-accent text-accent-foreground px-3 py-1">
                    MOST POPULAR
                  </div>
                )}
                <div className="label-eyebrow mb-3">{p.label}</div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="font-mono font-black text-5xl">${p.amount === 0 ? '0' : p.amount}</span>
                  <span className="text-sm opacity-70">/ month</span>
                </div>
                <p className="text-sm opacity-80 mb-6">{p.description}</p>

                <ul className="space-y-2 mb-8">
                  {features[k].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <Check size={16} weight="bold" className={isFeatured ? 'text-accent' : 'text-primary'} /> {f}
                    </li>
                  ))}
                </ul>

                <Button
                  className={`rounded-none w-full h-11 ${isFeatured ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''}`}
                  variant={isFeatured ? 'default' : (isCurrent ? 'outline' : 'default')}
                  disabled={isCurrent || loading[k]}
                  onClick={() => choose(k)}
                  data-testid={`pricing-choose-${k}`}
                >
                  {isCurrent ? 'Your plan' : loading[k] ? 'Redirecting...' : (p.amount === 0 ? 'Start free' : 'Upgrade')}
                  {!isCurrent && <ArrowRight size={16} className="ml-2" />}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center text-xs text-muted-foreground mono">
          TEST MODE · USE STRIPE TEST CARD 4242 4242 4242 4242 · ANY FUTURE DATE · ANY CVC
        </div>
      </div>
    </Shell>
  );
};

export default Pricing;
